import {Injectable} from "@angular/core";
import {ModalType} from "../../enums/global/modal-type.enum";
import {IconName} from "../../enums/global/icon-name.enum";
import {InfoCardConfigModel, InfoCardTextConfig} from "../../models/global/info-card-config.model";
import {Person} from "../../models/person.model";
import {AvatarUser} from "../../components/avatar-component/models/avatar-user.model";
import {FormGroup} from "@angular/forms";
import moment from "moment";
import CryptoJS from "crypto-js";
import {Router} from "@angular/router";
import {GdprRequestDetailsModel} from "../../models/gdpr/gdpr-request-details.model";
import {ContactState} from "../../enums/contact-state.enum";
import {AdvancedFilterValue} from "../../components/advanced-filter/_interfaces/advanced-filter-value.interface";
import {BehaviorSubject, Observable, take} from "rxjs";
import {PageSizeKeyEnum} from "../../enums/page-size-key.enum";

@Injectable({
  providedIn: 'root'
})
/**
 * The UtilsService provides utility methods that are used globally throughout the application.
 */
export class UtilsService {

  constructor(private readonly router: Router) {
  }

  /**
   * Returns the CSS class for the modal icon based on the given model type.
   * @param modalType modal type {@link ModalType}
   */
  cssClass(modalType: ModalType) {
    switch (modalType){
      case ModalType.SUCCESS:
        return 'featured-icon-primary-xl';
      case ModalType.ERROR:
        return 'featured-icon-error-xl';
      case ModalType.WARNING:
        return 'featured-icon-warning-xl';
      case ModalType.SECONDARY:
        return 'featured-icon-gray-xl';
      default:
        return 'featured-icon-primary-xl';
    }
  }

  /**
   * Returns the CSS class for the confirmation button based on the given model type.
   * @param modalType modal type {@link ModalType}
   */
  confirmButtonClass(modalType: ModalType) {
    switch (modalType){
      case ModalType.SUCCESS:
        return 'btn-primary';
      case ModalType.ERROR:
        return 'btn-danger';
      case ModalType.WARNING:
        return 'btn-danger';
      default:
        return 'btn-primary';
    }
  }

  /**
   * Checks if the specified GDPR request is cancelled or expired.
   * To be used to customize the GDPR accordion style and to know if we could send new GDPR requests or not
   * @param gdprRequest GDPR request {@link GdprRequestDetailsModel}
   */
  isGdprCanceledOrExpired(gdprRequest: GdprRequestDetailsModel):boolean {
    return !!gdprRequest.cancellationDate ||  moment() >= gdprRequest.expirationDate;
  }

  /**
   * Returns icon name to use in the modal on the given model type.
   * @param modalType modal type {@link ModalType}
   */
  modalIconName(modalType: ModalType) {
    switch (modalType){
      case ModalType.SUCCESS:
        return IconName.CHECKED_CIRCLE;
      case ModalType.ERROR:
        return IconName.ALERT_CIRCLE;
      case ModalType.WARNING:
        return IconName.ALERT_TRIANGLE;
      case ModalType.INFO:
        return IconName.INFO;
      default:
        return IconName.CHECKED_CIRCLE;
    }
  }

  /**
   * Builds and returns the config of awaiting GDPR preferences card info.
   * Displayed when a contact has an ongoing GDPR request or no requests at all.
   */
  getWaitingGDPRRequestCardConfig() {
    return new InfoCardConfigModel(
      ModalType.SECONDARY,
      new InfoCardTextConfig(
        'Awaiting GDPR Preferences',
        '20px'
      ),
      new InfoCardTextConfig(
        `We're waiting to receive this contact's GDPR preferences.
       <br>Once the contact submits their preferences, the information will be updated here.`,
        '12px'
      ),
      '400px',
      IconName.GDPR
    );
  }
  /**
   * Builds and returns the config of canceled GDPR preferences card info.
   */
  getGDPRRequestCanceledCardConfig() {
    return new InfoCardConfigModel(
      ModalType.ERROR,
      new InfoCardTextConfig(
        'This request has been canceled.',
        '20px'
      ),
      new InfoCardTextConfig(
        `The request you initiated has been canceled.`,
        '12px'
      ),
      '400px',
      IconName.GDPR
    );
  }

  /**
   * Builds and returns the config of an expired GDPR request card info.
   */
  getGDPRRequestExpiredCardConfig() {
    return new InfoCardConfigModel(
      ModalType.ERROR,
      new InfoCardTextConfig(
        'This request has expired.',
        '20px'
      ),
      new InfoCardTextConfig(
        `The request you initiated has expired, and no action was taken.`,
        '12px'
      ),
      '400px',
      IconName.GDPR
    );
  }

  /**
   * Builds and returns the config of No GDPR Preferences card info.
   * Displayed when a contact has an ongoing GDPR request or no requests at all.
   */
  getGDPRRequestCardConfig() {
    return new InfoCardConfigModel(
      ModalType.SECONDARY,
      new InfoCardTextConfig(
        'No GDPR Preferences',
        '20px'
      ),
      new InfoCardTextConfig(
        `No GDPR consent request has been sent yet.
       <br>Once a request is made and preferences are submitted by the contact, the information will be updated here.`,
        '12px'
      ),
      '400px',
      IconName.GDPR
    );
  }

  /**
   * Builds and returns the config of Disabled GDPR communication card info.
   * Displayed when a contact's GDPR communication are disabled.
   */
  getDisabledGDPRCommunicationCardConfig() {
    return new InfoCardConfigModel(
      ModalType.SECONDARY,
      new InfoCardTextConfig(
        'GDPR Communication Disabled',
        '20px'
      ),
      new InfoCardTextConfig(
        `The owner of this contact has disabled GDPR-related communication.`,
        '12px'
      ),
      '400px',
      IconName.GDPR
    );
  }

  createAvatarUsers(persons: Person[], check: boolean = false): AvatarUser[] {
    return persons.map((person: Person) => new AvatarUser(person, person.role, check));
  }

  /**
   * This method checks if a form control identified by the specified name
   * within the specified form group has errors of the specified validation type.
   * @param formGroup       form group {@link FormGroup}
   * @param controlName     form control name (ex: firstName,...)
   * @param validationType  validation applied to form control (ex: required,...)
   */
  isControlHasError(formGroup: FormGroup, controlName: string, validationType: string): boolean {
    let control = formGroup.get(controlName);
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  /**
   * Generates a SHA256 hash of the given object.
   */
  generateHash(obj: any): string {
    return CryptoJS.SHA256(JSON.stringify(obj)).toString();
  }


  /**
   * Adapts the given date to UTC timezone.
   *
   * @param date - The date to be adapted. It should be a moment.js Moment object.
   *
   * @returns The adapted date in UTC timezone. If the input date is null or undefined,
   *          the function will return null.
   */
  adaptUtcTimeZone(date: moment.Moment) {
    if (date) {
      // Convert the date to UTC without modifying the original date object
      return date.utc(true);
    } else {
      // Return null if the provided date is invalid
      return null;
    }
  }

  /**
   * Determines if a tooltip should be shown for a given text based on its length
   * and the available container width.
   *
   * @param text - The text to be evaluated for overflow.
   * @param containerWidth - The container width.
   * @returns `true` if the text exceeds the container width and requires a tooltip, otherwise `false`.
   *
   * */
  showTooltip(text: string, containerWidth: string | number) {
    // If text is null or undefined, no tooltip is required
    if (!text) return false;
    const width = typeof containerWidth === 'string' ? Number.parseFloat(containerWidth) : containerWidth;
    return this.doesTextOverflow(width, text);
  }

  /**
   * Determines whether the given text overflows a container of specified width in rem units.
   * Uses precise text measurement via Canvas API to account for actual font rendering.
   *
   * @param containerWidthRem - Container width in rem units (1rem = 12px in this system)
   * @param text - The text content to check for overflow
   * @param fontSizePx - Font size in pixels (default: 12px)
   * @returns `true` if text overflows container, `false` otherwise
   */
  doesTextOverflow(
    containerWidthRem: number,
    text: string,
    fontSizePx: number = 12
  ): boolean {
    if (!text?.length) return false;

    // Setup canvas for text measurement
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return false;

    // Set font style
    context.font = `${fontSizePx}px Poppins, sans-serif`;

    // Calculate text dimensions
    const textWidthPx = context.measureText(text).width;
    // Convert rem to pixels (1rem = 12px)
    const containerWidthPx = containerWidthRem * fontSizePx;
    // Return true if text exceeds container width (with 2px tolerance)
    return textWidthPx > containerWidthPx - 2;
  }

  /**
   * Prepares a URL link for a specific entity by encoding its ID and
   * generating a URL with said ID as a query parameter.
   *
   * This generated URL can be used to navigate to the specified target route,
   * with the side panel or other contextual information preloaded for that entity.
   *
   * @param entityId The ID of the entity to be encoded and used in the URL.
   * @param targetRoute The target route where the URL should navigate to (ex: 'contacts/all').
   * @returns A string representing the full URL, including the base origin and query parameters.
   */
  prepareEntityLink(entityId: number, targetRoute: string) {
    // Step 1: Encode the entity ID using Base64url encoding.
    const encodedEntityId = CryptoJS.enc.Base64url.stringify(
      CryptoJS.enc.Utf8.parse(entityId.toString())
    );

    // Step 2: Create a URL tree for the target route with the encoded entity ID as a query parameter.
    const urlTree = this.router.createUrlTree([targetRoute], {
      queryParams: { key: encodedEntityId },
    });

    // Step 3: Serialize the URL tree into a string, forming the full URL with base origin and query parameter.
    return globalThis.location.origin + this.router.serializeUrl(urlTree);
  }

  /**
   * Generates a deep link URL for a contact, encoding both the contact ID and optional status.
   * The data is encoded in a Base64URL format and passed as a single `key` query parameter.
   *
   * @param entityId - The unique identifier of the contact.
   * @param targetRoute - The route to navigate to (e.g., 'contacts/all').
   * @param status - Optional contact status to include in the encoded key (e.g., PENDING_VALIDATION).
   * @returns A full URL pointing to the target route with encoded query parameters.
   */
  createContactDeepLink(entityId: number, targetRoute: string, status?: ContactState): string {
    // Step 1: Prepare the raw string to encode: "id" or "id|status"
    let rawKey = entityId.toString();
    if (status) {
      rawKey += `|${status}`;
    }

    // Step 2: Encode the string using Base64URL
    const encodedKey = CryptoJS.enc.Base64url.stringify(
      CryptoJS.enc.Utf8.parse(rawKey)
    );

    // Step 3: Create a URL tree with the encoded key as query param
    const urlTree = this.router.createUrlTree([targetRoute], {
      queryParams: { key: encodedKey }
    });

    // Step 4: Return the fully-qualified URL
    return globalThis.location.origin + this.router.serializeUrl(urlTree);
  }

  /**
   * Recursively sorts the keys of an object and the values of arrays to ensure a consistent structure.
   * This is useful for generating stable hashes or performing deep comparisons.
   *
   * @param obj - The input object or array to be sorted.
   * @returns A new object or array with sorted keys and values.
   */
  sortObjectKeysAndValues(obj: any): any {
    const excludedKeys = new Set(['cities']);

    if (Array.isArray(obj)) {
      return obj
        .map(item => (typeof item === 'object' && item !== null ?
          this.sortObjectKeysAndValues(item) : item))
        .sort((a, b) => a?.localeCompare?.(b) ?? 0);
    }

    if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .filter(key => !excludedKeys.has(key))
        .sort()
        .reduce((acc: Record<string, any>, key: string) => {
          const val = (obj as Record<string, any>)[key];
          if (val === undefined) return acc;
          acc[key] = this.sortObjectKeysAndValues(val);
          return acc;
        }, {});
    }

    return obj;
  }

  /**
   * Compares two objects deeply by sorting their keys and serializing them to JSON.
   *
   * This method ensures consistent structure using key sorting, then checks
   * equality by comparing the resulting JSON strings.
   * Useful for detecting form changes or object differences regardless of key order.
   *
   * @param a - The first object to compare.
   * @param b - The second object to compare.
   * @returns True if both objects are deeply equal, false otherwise.
   */
  isDeepEqual(a: any, b: any): boolean {
    const sortedA = this.sortObjectKeysAndValues(a);
    const sortedB = this.sortObjectKeysAndValues(b);
    return JSON.stringify(sortedA) === JSON.stringify(sortedB);
  }


  /**
   * This function prepare data to initiate filter
   * @param {Array<{filterKey: string, value: any}>} filters - An array of filter objects containing:
   *   - `filterKey` (string): The key used in the filter.
   *   - `value` (any): The corresponding value.
   * @returns {AdvancedFilterValue[]} An array of processed filter values.
   */
  prepareFilter(filters: { filterKey: string, value: any }[]): AdvancedFilterValue[] {
    const initialFilter: AdvancedFilterValue[] = []
    filters.forEach(({ filterKey, value }) => {
      if (value) {
        const newFilter = {
          name: filterKey,
          options: [value]
        };
        initialFilter.push(newFilter)
      }
    });
    return initialFilter;
  }

  /**
   * Utility method to load options for multiple services only if their option list is currently empty.
   * This helps avoid redundant data loading by ensuring each service fetches data only once.
   * Uses `take(1)` to automatically unsubscribe after the first emission.
   *
   * @param services - An array of option services, each with an `options` BehaviorSubject
   * and a `getNextOptionsPage` method.
   */
  loadOptionsIfEmpty(...services: { options: BehaviorSubject<any[]>; getNextOptionsPage: () => Observable<any> }[])
    : void {
    services.forEach(service => {
      if (service.options.value.length === 0) {
        service.getNextOptionsPage().pipe(take(1)).subscribe();
      }
    });
  }

  // Filter persons by name and initials
  filterPersonsByNameAndInitials(persons: AvatarUser[], searchTerm: string): AvatarUser[] {
    return persons
      .filter(user => user.person.getFullName().toLowerCase().includes(searchTerm.toLowerCase()) || user.person.initials
        .toUpperCase()
        .includes(searchTerm.toUpperCase()));
  }

  /**
   * Get the pagination size for a specific table
   * @param key Table identifier from PageSizeKeyEnum
   * @param defaultValue Default size if not stored
   */
  getPaginationSize(key: PageSizeKeyEnum, defaultValue = 10): number {
    // Retrieve the stored pagination size JSON from localStorage
    const stored = localStorage.getItem(PageSizeKeyEnum.PAGINATION_SIZE);
    // Parse the JSON if it exists, otherwise create an empty object
    const settings = stored ? JSON.parse(stored) : {} as Record<PageSizeKeyEnum, number>;
    // Return the size for the requested table key, or the defaultValue if not found
    return settings[key] ?? defaultValue;
  }

  /**
   * Set the pagination size for a specific table
   * @param key Table identifier from PageSizeKeyEnum
   * @param size New page size to store
   */
  setPaginationSize(key: PageSizeKeyEnum, size: number): void {
    // Retrieve the stored pagination size JSON from localStorage
    const stored = localStorage.getItem(PageSizeKeyEnum.PAGINATION_SIZE);
    // Parse the JSON if it exists, otherwise create an empty object
    const settings = stored ? JSON.parse(stored) : {} as Record<PageSizeKeyEnum, number>;
    // Update the pagination size for the given table key
    settings[key] = size;
    // Save the updated settings back to localStorage as a JSON string
    localStorage.setItem(PageSizeKeyEnum.PAGINATION_SIZE, JSON.stringify(settings));
  }

  /**
   * Normalizes string values by:
   * - Trimming leading and trailing spaces
   * - Replacing multiple consecutive spaces with a single space
   */
  trimStringValues(obj: any): any {
    // If the current value is a string then trim spaces
    if (typeof obj === 'string') {
      const trimmed = obj.trim().replace(/\s+/g, ' ');
      return trimmed === '' ? null : trimmed;
    }

    // If the value is a Date, then add start of day in local timezone for consistent comparison
    if (obj instanceof Date) {
      // Create a new date at the start of the day to ignore time differences
      const initializedDate = new Date(obj.getFullYear(), obj.getMonth(), obj.getDate());
      return initializedDate.toISOString();
    }

    // If the value is an array then trim spaces recursively from each item
    if (Array.isArray(obj)) {
      return obj.map(item => this.trimStringValues(item));
    }

    // If the value is an object then trim spaces from each property
    if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc: { [key: string]: any }, key) => {
        acc[key] = this.trimStringValues(obj[key]);
        return acc;
      }, {});
    }

    return obj;
  }
}
